import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, PenLine, FileText } from "lucide-react";
import { getGuestSession, signGuestSession, DOC_TYPES } from "@/lib/afmApi";
import { Logo } from "@/components/Logo";
import { SignaturePad } from "@/components/SignaturePad";
import { Loading, ErrorState } from "@/components/common";

const ACCENT = "#9E1B22";
const Card = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-[#E5E5E5] bg-white p-5">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1 font-display text-4xl font-bold" style={{ color: ACCENT }} data-testid={testId}>{value}</div>
  </div>
);

export default function GuestSign() {
  const { token } = useParams();
  const [g, setG] = useState(null);
  const [status, setStatus] = useState("loading");
  const [sig, setSig] = useState(null);
  const [signing, setSigning] = useState(false);

  const load = () => {
    setStatus("loading");
    getGuestSession(token).then((d) => { setG(d); setStatus("ok"); }).catch(() => setStatus("error"));
  };
  useEffect(load, [token]);

  const submit = async () => {
    if (!sig) { toast.error("Dibuja tu firma primero"); return; }
    setSigning(true);
    try {
      await signGuestSession(token, { signature_data_url: sig, biometric_verified: false });
      toast.success("¡Firma registrada!");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo registrar la firma");
    } finally { setSigning(false); }
  };

  if (status === "loading") return <Loading />;
  if (status === "error" || !g) return <ErrorState onRetry={load} />;
  const me = g.me || {};
  const signed = g.already_signed || g.sealed;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#171717]" data-testid="guest-sign-page">
      <header className="border-b border-black/10"><div className="mx-auto flex max-w-2xl items-center px-5 py-4"><Link to="/"><Logo imgClassName="h-10 w-auto object-contain" /></Link></div></header>
      <main className="mx-auto max-w-2xl px-5 py-14">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}><FileText className="h-3.5 w-3.5" /> {DOC_TYPES[g.doc_type] || "Acuerdo"} · Firma de colaborador</div>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight lg:text-4xl" data-testid="guest-title">{g.project_title || g.fonograma_title}</h1>
        <p className="mt-2 text-sm text-slate-600">Hola {me.name || me.email}, {g.producer_name ? <><strong>{g.producer_name}</strong> te asignó</> : "se te asignó"} el siguiente reparto{g.session_date ? ` (fecha: ${g.session_date})` : ""}. Revísalo y firma para dejar constancia de tu aceptación.</p>

        <div className="mt-8 grid grid-cols-2 gap-4">
          <Card label="Máster" value={`${me.master_pct ?? 0}%`} testId="guest-master" />
          <Card label="Publishing" value={`${me.publishing_pct ?? 0}%`} testId="guest-publishing" />
        </div>
        <div className="mt-4 grid gap-2 rounded-2xl border border-[#E5E5E5] bg-white p-5 text-sm sm:grid-cols-2">
          <div><span className="text-slate-500">Rol:</span> <strong className="capitalize">{me.role}</strong></div>
          <div><span className="text-slate-500">Derechos:</span> <strong className="capitalize">{me.rights}</strong></div>
          {me.id_document && <div><span className="text-slate-500">Documento:</span> <strong>{me.id_document}</strong></div>}
          {me.country && <div><span className="text-slate-500">País:</span> <strong>{me.country}</strong></div>}
        </div>

        {signed ? (
          <div className="mt-8 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800" data-testid="guest-signed">
            <CheckCircle2 className="h-6 w-6" /><div><div className="font-semibold">{g.sealed ? "Acuerdo sellado" : "Tu firma quedó registrada"}</div><div className="text-xs">{g.sealed ? "Todas las partes firmaron. El PDF sellado está disponible para el creador del acuerdo." : "Recibirás el PDF sellado cuando todas las partes firmen."}</div></div>
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-[#E5E5E5] bg-white p-5">
            <div className="flex items-center gap-2 text-sm font-semibold"><PenLine className="h-4 w-4" /> Tu firma</div>
            <div className="mt-3"><SignaturePad onChange={setSig} /></div>
            <button data-testid="guest-sign-button" onClick={submit} disabled={signing || !sig} className="mt-4 w-full rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>{signing ? "Registrando…" : "Firmar y aceptar"}</button>
            <p className="mt-3 text-[11px] text-slate-500">Al firmar aceptas el reparto indicado. La firma queda registrada con fecha y hora en el backend compartido SONA.</p>
          </div>
        )}
      </main>
    </div>
  );
}
